import * as am4core from '@amcharts/amcharts4/core';
import { Color, color } from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { ColumnSeries, XYChart } from '@amcharts/amcharts4/charts';
import {
  setupBaseChart,
  setupLegendBase,
  setupMarkerColumnBase,
  setupSeriesTooltipBase,
  setupValueAxisBase,
} from 'features/shared/ui/charts';
import { numberWithSpaces } from 'utils/numberWithSpaces';
import { colors } from 'features/schools/services/colors';
import { ChartData, ChartDataWithDiff } from 'features/schools/services/models';

function createLabelBullet(series: ColumnSeries) {
  const bullet = series.bullets.push(new am4charts.LabelBullet());
  bullet.label.text = '{valueY}';
  bullet.locationY = 0.5;
  bullet.label.adapter.add('textOutput', (text) => numberWithSpaces(+text));

  const fillColor = color(series.fill as Color);
  fillColor.darkColor = am4core.color('rgba(9, 21, 38, 0.35)');
  bullet.label.adapter.add('fill', () => fillColor.alternative);
  bullet.label.hideOversized = true;
}

function createSeries(
  chart: am4charts.XYChart,
  key: string,
  tooltipTotalKey: string,
  name: string,
  isTotal: boolean,
  showBullets: boolean,
) {
  const series = chart.series.push(new am4charts.ColumnSeries());

  setupSeriesTooltipBase(series);

  series.dataFields.valueY = key;
  series.dataFields.categoryX = 'classes';
  series.name = name;
  series.stacked = true;
  series.stroke = color('white');

  if (isTotal) {
    series.hiddenInLegend = true;
    series.fillOpacity = 0.3;
    series.columns.template.tooltipText = `{name} (всего) {${tooltipTotalKey}}`;
  } else {
    series.columns.template.tooltipText = '{name} {valueY}';
  }

  if (showBullets) {
    createLabelBullet(series);
  }
}

function createFakeTotalSeries(chart: XYChart, noneKey: string) {
  const series = chart.series.push(new am4charts.ColumnSeries());
  series.dataFields.valueY = noneKey;
  series.dataFields.categoryX = 'classes';
  series.stacked = true;
  series.hiddenInLegend = true;
  series.strokeOpacity = 0;
}

function calcTotalDiff(data: ChartData): ChartDataWithDiff {
  const result = data.subjects.reduce((acc, el) => {
    return { ...acc, [el + '_to_total']: data[el + '_total'] - data[el] || 0 };
  }, {} as Record<string, number>);
  return {
    ...data,
    ...result,
    none: 0,
  } as ChartDataWithDiff;
}

function doubleColorsForTotals(colors: Color[]) {
  return colors.reduce((prev: am4core.Color[], next) => {
    prev.push(next, next);
    return prev;
  }, []);
}

function createAllSeries(chart: XYChart, subjects: string[], showTotal?: boolean) {
  subjects.forEach((el) => {
    createSeries(chart, el, '', el, false, !showTotal);
    if (showTotal) {
      createSeries(chart, el + '_to_total', el + '_total', el, true, !showTotal);
    }
  });

  if (!showTotal) {
    createFakeTotalSeries(chart, 'none');
  }
}

export function setupChart(element: HTMLDivElement, data: ChartData[], showTotal: undefined | boolean) {
  const chart = am4core.create(element, am4charts.XYChart);
  setupBaseChart(chart);
  // chart.paddingBottom = 24;
  // chart.paddingLeft = -10;
  chart.data = data.map(calcTotalDiff);

  const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
  categoryAxis.dataFields.category = 'classes';
  categoryAxis.renderer.grid.template.disabled = true;
  categoryAxis.renderer.labels.template.fill = am4core.color('rgba(9, 21, 38, 0.85)');
  categoryAxis.renderer.labels.template.dy = 10;

  const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
  setupValueAxisBase(valueAxis);
  valueAxis.extraMax = 0.25;
  valueAxis.renderer.labels.template.fontSize = 16;
  valueAxis.renderer.minGridDistance = 50;
  valueAxis.min = 0;
  valueAxis.maxPrecision = 0;
  valueAxis.calculateTotals = true;

  chart.colors.list = showTotal ? doubleColorsForTotals(colors) : colors;

  createAllSeries(
    chart,
    data
      .map((el) => el.subjects)
      .flat()
      .filter((el, i, arr) => arr.indexOf(el) === i),
    showTotal,
  );

  chart.legend = setupLegendBase(true);
  chart.legend.dy = 24;
  chart.legend.itemContainers.template.paddingLeft = 20;

  const marker = chart.legend.markers.template;
  setupMarkerColumnBase(marker, false);

  return chart;
}
